// features/admin/rewardSettingsApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface RewardSetting {
  id: number;
  min_amount: string;
  max_amount: string;
  points: string;
  created_at?: string;
}

export interface RewardSettingFormData {
  min_amount: string;
  max_amount: string;
  points: string;
  id?: number;
}

export interface RewardSettingResponse {
  error: boolean;
  message?: string;
  data?: RewardSetting[];
}

export interface RewardSettingRequest {
  min_amount: string;
  max_amount: string;
  points: string;
}

export interface UpdateRewardSettingRequest extends RewardSettingRequest {
  id: number;
}

export interface DeleteRewardSettingRequest {
  id: number;
}

// Validation helpers
export const validateAmount = (value: string): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0;
};

export const validatePoints = (value: string): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0;
};

export const validateAmountRange = (minAmount: string, maxAmount: string): boolean => {
  const min = parseFloat(minAmount);
  const max = parseFloat(maxAmount);
  return !isNaN(min) && !isNaN(max) && min < max;
};

export const rewardSettingsApi = createApi({
  reducerPath: 'rewardSettingsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['RewardSetting'],
  endpoints: (build) => ({
    listRewardSettings: build.query<RewardSettingResponse, void>({
      query: () => ({
        url: 'user/rewardsettinglist',
        method: 'POST',
        body: new URLSearchParams(),
      }),
      providesTags: ['RewardSetting'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || '',
        data: response.data || [],
      }),
    }),

    addRewardSetting: build.mutation<RewardSettingResponse, RewardSettingRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('min_amount', data.min_amount.trim());
        formData.append('max_amount', data.max_amount.trim());
        formData.append('points', data.points.trim());

        return {
          url: 'user/rewardsettingadd',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['RewardSetting'],
    }),

    updateRewardSetting: build.mutation<RewardSettingResponse, UpdateRewardSettingRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('id', data.id.toString());
        formData.append('min_amount', data.min_amount.trim());
        formData.append('max_amount', data.max_amount.trim());
        formData.append('points', data.points.trim());

        return {
          url: 'user/rewardsettingupdate',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['RewardSetting'],
    }),

    deleteRewardSetting: build.mutation<RewardSettingResponse, DeleteRewardSettingRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('id', data.id.toString());

        return {
          url: 'user/rewardsettingdelete',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['RewardSetting'],
    }),
  }),
});

export const {
  useListRewardSettingsQuery,
  useAddRewardSettingMutation,
  useUpdateRewardSettingMutation,
  useDeleteRewardSettingMutation,
} = rewardSettingsApi;